import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import MobilePageLayout from '../../../components/layout/MobilePageLayout';
import { convertToVwMob } from '../../../Utils/responsive';
import { useNavigate } from 'react-router-dom';
import mainLogo from '../../../assets/Logo/main_logo.png';
import kakaoIcon from '../../../assets/Icon/footer/kakao_icon.png';
import adBannerMob from '../../../assets/Icon/project/ad_banner_mob.png';
import engBannerMob from '../../../assets/Icon/project/Eng_Mobile.png';
import loudspeakerIcon from '../../../assets/Icon/project/loudspeaker_icon.png';
import donateIcon from '../../../assets/Icon/project/donate_icon.png';
import { useLogin } from '../useLogin';
import { PATHS } from '../../../routes/paths';
import {
  MISSIONFUND_PRIVACY_POLICY_URL,
  MISSIONFUND_TERMS_OF_SERVICE_URL,
} from '../../../Utils/missionFundLegalUrls';

// 모금자/후원자 선택은 추후 다시 활성화 예정입니다.
const SHOW_LOGIN_TYPE_TABS: boolean = false;

const LoginMob = () => {
  const { i18n, t } = useTranslation('translation');
  const navigate = useNavigate();
  const currentLang = (i18n.resolvedLanguage || i18n.language || 'ko').split('-')[0];
  const {
    loginType,
    setLoginType,
    email,
    setEmail,
    password,
    setPassword,
    handleLogin,
    handleKakaoDonorLogin,
    isBusy,
    postSignupHint,
    error,
  } = useLogin();
  const [showPassword, setShowPassword] = React.useState(false);
  const bannerImage = currentLang === 'en' ? engBannerMob : adBannerMob;
  const visibleLoginType = SHOW_LOGIN_TYPE_TABS ? loginType : 'fundraiser';

  return (
    <MobilePageLayout title="" hideHeader hideFooter>
      <HeaderRow>
        <LangSelect
          value={currentLang}
          onChange={(e) => i18n.changeLanguage(e.target.value)}
        >
          <option value="ko">{t('lang.ko')}</option>
          <option value="en">{t('lang.en')}</option>
        </LangSelect>
      </HeaderRow>
      <Container>
        <LogoSection>
          <LogoLink href="https://missionfund.org/" target="_blank" rel="noopener noreferrer" aria-label="MissionFund homepage">
            <Logo src={mainLogo} alt="Mission Fund" />
          </LogoLink>
        </LogoSection>

        {/* Temporarily hidden: fundraiser/donor selector will be restored later. */}
        {SHOW_LOGIN_TYPE_TABS ? (
          <TabWrapper>
            <Tab
              $active={loginType === 'fundraiser'}
              onClick={() => setLoginType('fundraiser')}
            >
              <TabIcon $active={loginType === 'fundraiser'} $icon={loudspeakerIcon} /> {t('page.login.tabsShort.fundraiser')}
            </Tab>
            <Tab
              $active={loginType === 'donor'}
              onClick={() => setLoginType('donor')}
            >
              <TabIcon $active={loginType === 'donor'} $icon={donateIcon} /> {t('page.login.tabsShort.donor')}
            </Tab>
          </TabWrapper>
        ) : null}

        <FormSection>
          {visibleLoginType === 'fundraiser' ? (
            <>
              <Form onSubmit={handleLogin}>
                {postSignupHint && (
                  <HintMessage>{t('page.login.messages.signupCompleteSignIn')}</HintMessage>
                )}
                <InputWrapper>
                  <InputFieldWrapper>
                    <Input
                      type="email"
                      placeholder={t('page.login.labels.email')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                    {email && (
                      <ClearButton type="button" onClick={() => setEmail('')}>
                        <ClearIcon />
                      </ClearButton>
                    )}
                  </InputFieldWrapper>
                  <InputFieldWrapper>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('page.login.labels.password')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    {password && (
                      <InputActions>
                        <TogglePasswordButton
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                        </TogglePasswordButton>
                        <ClearButton type="button" onClick={() => setPassword('')}>
                          <ClearIcon />
                        </ClearButton>
                      </InputActions>
                    )}
                  </InputFieldWrapper>
                </InputWrapper>
                <SubmitButton type="submit" disabled={isBusy || !email || !password}>
                  {isBusy ? t('page.login.actions.loggingIn') : t('page.login.actions.login')}
                </SubmitButton>
                {error && <ErrorMessage>{error}</ErrorMessage>}
              </Form>
            </>
            ) : (
              <DonorSection>
                <DonorText>{t('page.login.donor.ctaMob')}</DonorText>
                <KakaoButton type="button" onClick={handleKakaoDonorLogin}>
                  <KakaoIcon src={kakaoIcon} alt="Kakao" /> {t('page.login.donor.kakaoLogin')}
                </KakaoButton>
              </DonorSection>
            )}
        </FormSection>

        {visibleLoginType === 'donor' && (
          <DonorHistoryText type="button" onClick={() => navigate(PATHS.DONATION_HISTORY_FIND)}>
            {t('page.login.donor.historyLink')}
          </DonorHistoryText>
        )}

        {visibleLoginType === 'fundraiser' && (
          <LinkSection>
            <LinkText onClick={() => navigate(PATHS.PASSWORD_FIND)}>{t('page.login.links.findPassword')}</LinkText>
            <LinkDivider>|</LinkDivider>
            <LinkText $highlight onClick={() => navigate(PATHS.SIGNUP)}>
              {t('page.login.links.signup')}
            </LinkText>
          </LinkSection>
        )}

        <BannerSection href="https://missionfund.org/support/" target="_blank" rel="noopener noreferrer">
          <BannerImage src={bannerImage} alt={t('page.login.bannerAlt')} />
        </BannerSection>

        <FooterLinks>
          <FooterLegalLink
            href={MISSIONFUND_TERMS_OF_SERVICE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('common.footer.servicePolicy')}
          </FooterLegalLink>
          <FooterLinkDivider />
          <FooterLegalLink
            href={MISSIONFUND_PRIVACY_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('common.footer.privacyPolicy')}
          </FooterLegalLink>
        </FooterLinks>

      </Container>
    </MobilePageLayout>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-bottom: ${convertToVwMob(40)};
`;

const HeaderRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const LangSelect = styled.select`
  padding: ${convertToVwMob(12)} ${convertToVwMob(36)} ${convertToVwMob(12)} ${convertToVwMob(16)};
  border-radius: ${convertToVwMob(8)};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  background: white;
  color: ${({ theme }) => theme.colors.grayScale[70]};
  font-size: ${convertToVwMob(18)};
  cursor: pointer;
  outline: none;
  min-height: ${convertToVwMob(44)};
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='14' height='8' viewBox='0 0 14 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L7 7L13 1' stroke='%23929292' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right ${convertToVwMob(12)} center;
  background-size: ${convertToVwMob(14)} ${convertToVwMob(8)};
`;

const LogoSection = styled.div`
  margin-top: ${convertToVwMob(40)};
  margin-bottom: ${convertToVwMob(40)};
`;

const Logo = styled.img`
  width: ${convertToVwMob(163)};
  height: ${convertToVwMob(50)};
  object-fit: contain;
`;

const LogoLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const TabWrapper = styled.div`
  display: flex;
  width: 100%;
  background: #EEEEEE;
  border-radius: ${convertToVwMob(30)};
  padding: ${convertToVwMob(4)};
  margin-bottom: ${convertToVwMob(28)};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[10]};
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: ${convertToVwMob(10)} 0;
  border: ${({ $active }) => ($active ? '2px solid #2BC2E1' : 'none')};
  border-radius: ${convertToVwMob(26)};
  background: ${({ $active, theme }) => ($active ? theme.colors.grayScale[0] : '#EEEEEE')};
  color: ${({ $active, theme }) => ($active ? theme.colors.grayScale[90] : theme.colors.grayScale[70])};
  ${({ theme }) => theme.typography.contents.medium20}
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(6)};
  box-shadow: ${({ $active }) => ($active ? '0px 2px 8px rgba(0, 0, 0, 0.05)' : 'none')};
  transition: all 0.2s ease-in-out;
`;

const TabIcon = styled.div<{ $active: boolean; $icon: string }>`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(18)};
  background-color: ${({ $active, theme }) => ($active ? '#2BC2E1' : theme.colors.grayScale[70])};
  mask: ${({ $icon }) => `url(${$icon}) no-repeat center / contain`};
  -webkit-mask: ${({ $icon }) => `url(${$icon}) no-repeat center / contain`};
`;

const FormSection = styled.div`
  width: 100%;
  background: transparent;
  margin-bottom: 0;
`;

const DonorSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwMob(20)};
  padding: ${convertToVwMob(12)} 0;
`;

const DonorText = styled.div`
  width: 313px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  opacity: 1;
  ${({ theme }) => theme.typography.contents.regular20}
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const KakaoButton = styled.button`
    width: ${convertToVwMob(353)};
    height: ${convertToVwMob(60)};
  padding: ${convertToVwMob(14)};
  background: #fee500;
  color: #191919;
  border: none;
  border-radius: ${convertToVwMob(12)};
  font-size: ${convertToVwMob(20)};
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  cursor: pointer;
`;

const DonorHistoryText = styled.button`
  margin: ${convertToVwMob(12)} 0 ${convertToVwMob(64)};
  color: #17171B;
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: auto;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
`;

const KakaoIcon = styled.img`
  width: ${convertToVwMob(36)};
  height: ${convertToVwMob(36)};
  object-fit: contain;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0;
`;

const InputFieldWrapper = styled.div`
  display: flex;
  align-items: center;
  width: ${convertToVwMob(353)};
  height: ${convertToVwMob(70)};
  padding: ${convertToVwMob(14)};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[50]};
  background: white;
  color: ${({ theme }) => theme.colors.grayScale[60]};
  gap: ${convertToVwMob(8)};
  position: relative;
  z-index: 1;

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primaryBlue[40]};
    z-index: 2;
  }

  & + & {
    margin-top: -1px;
  }

  &:first-child {
    border-top-left-radius: ${convertToVwMob(12)};
    border-top-right-radius: ${convertToVwMob(12)};
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  &:last-child {
    border-bottom-left-radius: ${convertToVwMob(12)};
    border-bottom-right-radius: ${convertToVwMob(12)};
  }
`;

const Input = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: ${convertToVwMob(20)};
  font-weight: 500;
  background: transparent;
  padding: 0;
  min-width: 0;

  &::placeholder {
    color: ${({ theme }) => theme.colors.grayScale[40]};
  }
`;

const InputActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(6)};
`;

const ClearButton = styled.button`
  width: ${convertToVwMob(22)};
  height: ${convertToVwMob(22)};
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const TogglePasswordButton = styled.button`
  width: ${convertToVwMob(22)};
  height: ${convertToVwMob(22)};
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const ClearIcon = () => (
  <svg
    width={convertToVwMob(22)}
    height={convertToVwMob(22)}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="10" fill="#BCBCBC" />
    <path d="M8 8L16 16M16 8L8 16" stroke="white" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const EyeIcon = () => (
  <svg
    width={convertToVwMob(22)}
    height={convertToVwMob(22)}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 5C7 5 2.73 8.11 1 12.5C2.73 16.89 7 20 12 20C17 20 21.27 16.89 23 12.5C21.27 8.11 17 5 12 5ZM12 17.5C9.24 17.5 7 15.26 7 12.5C7 9.74 9.24 7.5 12 7.5C14.76 7.5 17 9.74 17 12.5C17 15.26 14.76 17.5 12 17.5ZM12 9.5C10.34 9.5 9 10.84 9 12.5C9 14.16 10.34 15.5 12 15.5C13.66 15.5 15 14.16 15 12.5C15 10.84 13.66 9.5 12 9.5Z"
      fill="#BCBCBC"
    />
  </svg>
);

const EyeOffIcon = () => (
  <svg
    width={convertToVwMob(22)}
    height={convertToVwMob(22)}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 6.5C15.79 6.5 19.17 8.63 20.82 12C20.23 13.31 19.4 14.41 18.41 15.27L19.82 16.68C21.21 15.45 22.31 13.85 23 12C21.27 7.61 17 4.5 12 4.5C10.73 4.5 9.51 4.7 8.36 5.07L10.01 6.72C10.66 6.58 11.32 6.5 12 6.5ZM2.71 3.16L4.69 5.14C2.97 6.42 1.61 8.12 1 12C2.73 16.39 7 19.5 12 19.5C13.52 19.5 14.98 19.21 16.32 18.67L19.03 21.38L20.44 19.97L4.12 3.75L2.71 3.16ZM12 17.5C8.21 17.5 4.83 15.37 3.18 12C3.88 10.49 4.9 9.21 6.13 8.27L8.24 10.38C8.09 10.9 8 11.44 8 12C8 14.21 9.79 16 12 16C12.56 16 13.1 15.91 13.62 15.76L15.26 17.4C14.22 17.78 13.13 18 12 17.5ZM11.86 9.51L14.49 12.14C14.46 10.65 13.35 9.54 11.86 9.51Z"
      fill="#BCBCBC"
    />
  </svg>
);

const HintMessage = styled.div`
  color: #026478;
  font-size: ${convertToVwMob(14)};
  text-align: center;
  margin-bottom: ${convertToVwMob(12)};
  font-weight: 500;
  line-height: 150%;
  width: 100%;
  max-width: ${convertToVwMob(353)};
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.other.red10};
  font-size: ${convertToVwMob(14)};
  text-align: center;
  margin-top: ${convertToVwMob(12)};
  font-weight: 500;
`;

const SubmitButton = styled.button`
  width: ${convertToVwMob(353)};
  height: ${convertToVwMob(60)};
  padding: ${convertToVwMob(14)};
  background: ${({ disabled, theme }) => (disabled ? theme.colors.grayScale[50] : theme.colors.primaryBlue[80])};
  color: ${({ disabled }) => (disabled ? '#EEEEEE' : '#FFFFFF')};
  border: none;
  border-radius: ${convertToVwMob(12)};
  font-size: ${convertToVwMob(20)};
  font-weight: 700;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  margin-top: ${convertToVwMob(20)};
  transition: all 0.2s ease-in-out;
  
  &:active:not(:disabled) {
    background: #014d5c;
    transform: scale(0.98);
  }
`;

const LinkSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${convertToVwMob(12)};
  margin-top: ${convertToVwMob(12)};
`;

const LinkText = styled.button<{ $highlight?: boolean }>`
  background: none;
  border: none;
  padding: 0;
  color: ${({ $highlight }) => ($highlight ? '#2BC2E1' : '#17171B')};
  font-family: 'Pretendard', sans-serif;
  font-size: ${convertToVwMob(20)};
  font-weight: ${({ $highlight }) => ($highlight ? 500 : 400)};
  line-height: 150%;
  cursor: pointer;
`;

const LinkDivider = styled.span`
  color: #eee;
  font-family: 'Pretendard', sans-serif;
  font-size: ${convertToVwMob(24)};
  font-weight: 400;
  line-height: normal;
`;

const BannerSection = styled.a`
  width: 100%;
  cursor: pointer;
  margin-top: ${convertToVwMob(54)};
`;

const BannerImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const FooterLinks = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  gap: ${convertToVwMob(6)};
  margin-top: ${convertToVwMob(24)};
  padding: 0 ${convertToVwMob(20)};
  margin-bottom: 10px;
`;

const FooterLegalLink = styled.a`
  font-size: ${convertToVwMob(16)};
  line-height: 100%;
  color: ${({ theme }) => theme.colors.grayScale[60]};
  cursor: pointer;
  white-space: nowrap;
  font-weight: 500;
  font-family: Pretendard;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colors.grayScale[80]};
    text-decoration: underline;
  }
`;

const FooterLinkDivider = styled.div`
  width: 1px;
  height: ${convertToVwMob(12)};
  background: ${({ theme }) => theme.colors.grayScale[50]};
`;

export default LoginMob;
